import {
  AppShell,
  Burger,
  Group,
  Loader,
  MantineProvider,
  Menu,
  ActionIcon,
  Text,
  Title,
  Select,
  useMantineTheme,
} from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { Notifications } from "@mantine/notifications";
import { IconLogout, IconUser, IconCalendarEvent } from "@tabler/icons-react";
import { PropsWithChildren, Suspense, useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useLocation } from "wouter";

import AdminSidebar from "./AdminSidebar";

import { ErrorFallback } from "@/shared/components/ErrorFallback";
import FillAndCenter from "@/shared/components/FillAndCenter";

import { AdminNavProvider } from "@/features/admin/contexts/AdminNavContext";
import { useAdminAuthStore } from "@/features/admin/stores/useAdminAuthStore";
import { useAdminEventDataStore } from "@/features/admin/stores/useAdminEventDataStore";

function PageLoader() {
  return (
    <div
      style={{
        position: "relative",
        width: "var(--app-shell-content-width)",
        height: "var(--app-shell-content-height)",
      }}
    >
      <FillAndCenter>
        <Loader size="lg" />
      </FillAndCenter>
    </div>
  );
}

function AdminLayoutContent({ children }: PropsWithChildren) {
  const theme = useMantineTheme();
  const [, setLocation] = useLocation();
  const { user, logoutUser } = useAdminAuthStore();

  // Event selection state from unified store
  const selectedEventId = useAdminEventDataStore(
    (state) => state.selectedEventId,
  );
  const events = useAdminEventDataStore((state) => state.events);
  const setSelectedEventId = useAdminEventDataStore(
    (state) => state.setSelectedEventId,
  );
  const fetchEventList = useAdminEventDataStore(
    (state) => state.fetchEventList,
  );
  const startPolling = useAdminEventDataStore((state) => state.startPolling);
  const stopPolling = useAdminEventDataStore((state) => state.stopPolling);

  const [mobileOpened, { toggle: toggleMobile, close: closeMobile }] =
    useDisclosure();
  const [desktopOpened, { toggle: toggleDesktop }] = useDisclosure(true);

  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

  const navContextValue = {
    closeMobileNav: () => {
      if (!isMobile) return;
      closeMobile();
    },
  };

  const handleLogout = async () => {
    await logoutUser();
    setLocation("/login");
  };

  // Load events and start polling on mount
  useEffect(() => {
    fetchEventList();
    startPolling();

    return () => {
      stopPolling();
    };
  }, [fetchEventList, startPolling, stopPolling]);

  // When event selection changes, data is automatically cleared and refetched by the store
  const handleEventChange = (value: string | null) => {
    const eventId = value === "current" ? null : value ? parseInt(value) : null;
    setSelectedEventId(eventId);
  };

  useEffect(() => {
    document.body.classList.add("adminLayout");

    return () => {
      document.body.classList.remove("adminLayout");
    };
  }, []);

  // Admin pages with header and sidebar
  return (
    <AdminNavProvider value={navContextValue}>
      <AppShell
        header={{ height: 60 }}
        navbar={{
          width: 200,
          breakpoint: "sm",
          collapsed: { mobile: !mobileOpened, desktop: !desktopOpened },
        }}
        padding="md"
        style={{
          "--app-shell-content-width":
            "calc(100vw - var(--app-shell-navbar-offset) - var(--app-shell-padding) * 2)",
          "--app-shell-content-height":
            "calc(100dvh - var(--app-shell-header-offset) - var(--app-shell-padding) * 2)",
        }}
      >
        <AppShell.Header>
          <Group h="100%" px="md" justify="space-between">
            <Group>
              <Burger
                opened={mobileOpened}
                onClick={toggleMobile}
                hiddenFrom="sm"
                size="sm"
              />
              <Burger
                opened={desktopOpened}
                onClick={toggleDesktop}
                visibleFrom="sm"
                size="sm"
              />
              <Title order={3}>Empower x Broncos Admin</Title>
            </Group>

            <Group>
              {/* Event Selector */}
              <Select
                leftSection={<IconCalendarEvent size={16} />}
                placeholder="Select Event"
                value={
                  selectedEventId === null ? "current" : String(selectedEventId)
                }
                onChange={handleEventChange}
                data={[
                  { value: "current", label: "📍 Current Event" },
                  ...events.map((event) => ({
                    value: String(event.event_id),
                    label: `📁 ${event.name} (${event.submission_count} submissions)`,
                  })),
                ]}
                w={250}
                size="sm"
                comboboxProps={{ withinPortal: true }}
              />

              <Menu shadow="md" width={200}>
                <Menu.Target>
                  <ActionIcon variant="subtle" size="lg">
                    <IconUser size={20} />
                  </ActionIcon>
                </Menu.Target>

                <Menu.Dropdown>
                  <Menu.Label>
                    <Text size="sm" fw={500}>
                      {user?.username || "Admin"}
                    </Text>
                  </Menu.Label>
                  <Menu.Divider />
                  <Menu.Item
                    leftSection={<IconLogout size={16} />}
                    onClick={handleLogout}
                    color="red"
                  >
                    Logout
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Group>
        </AppShell.Header>

        <AppShell.Navbar>
          <AdminSidebar />
        </AppShell.Navbar>

        <AppShell.Main>
          <Suspense fallback={<PageLoader />}>{children}</Suspense>
        </AppShell.Main>
      </AppShell>
    </AdminNavProvider>
  );
}

export default function AdminLayout({ children }: PropsWithChildren) {
  return (
    <MantineProvider>
      <Notifications />
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <AdminLayoutContent>{children}</AdminLayoutContent>
      </ErrorBoundary>
    </MantineProvider>
  );
}
